/**
 * This context is used to store the state of the opening which is currently stored in the the zustand store
 * But this is a global state and we want to use it in the opening page alone
 * Eventually everything from the zustand store will be moved to this context
 */

import {
  SourcingHiringGoalWithMetrics,
  SourcingOpeningDetailAnalyticsData,
  SourcingOpeningDetailsResponse,
} from 'api-clients/monolith';
import { createContext, useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type OpeningContextType = {
  showAggregateData: boolean;
  setShowAggregateData: (showAggregateData: boolean) => void;
  analyticsData?: SourcingOpeningDetailAnalyticsData;
  setAnalyticsData: (analytics: SourcingOpeningDetailAnalyticsData) => void;
  showCheckbox: boolean;
  setShowCheckbox: (showCheckbox: boolean) => void;
  hiringGoal?: SourcingHiringGoalWithMetrics;
  setHiringGoal: (hiringGoal: SourcingHiringGoalWithMetrics) => void;
  openingTitle?: SourcingOpeningDetailsResponse['title'];
  setOpeningTitle: (title: SourcingOpeningDetailsResponse['title']) => void;
  hiringGoalUrl?: string;
  setHiringGoalUrl: (url: string) => void;
  currentHiringGoalId?: string;
};

export const OpeningContext = createContext<OpeningContextType | undefined>(
  undefined,
);

export function OpeningProvider({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const currentHiringGoalId = searchParams.get('hiring_goal_id') ?? undefined;

  const [showAggregateData, setShowAggregateData] = useState(true);
  const [analyticsData, setAnalyticsData] =
    useState<SourcingOpeningDetailAnalyticsData>();
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [hiringGoal, setHiringGoal] = useState<SourcingHiringGoalWithMetrics>();
  const [openingTitle, setOpeningTitle] =
    useState<SourcingOpeningDetailsResponse['title']>();
  const [hiringGoalUrl, setHiringGoalUrl] = useState<string>();

  return (
    <OpeningContext.Provider
      value={{
        showAggregateData,
        setShowAggregateData,
        analyticsData,
        setAnalyticsData,
        showCheckbox,
        setShowCheckbox,
        hiringGoal,
        setHiringGoal,
        openingTitle,
        setOpeningTitle,
        hiringGoalUrl,
        setHiringGoalUrl,
        currentHiringGoalId,
      }}
    >
      {children}
    </OpeningContext.Provider>
  );
}

export function useOpeningContext() {
  const context = useContext(OpeningContext);

  if (!context) {
    throw new Error('useOpeningContext must be used within an OpeningProvider');
  }

  return context;
}
